<template>
  <div class="tc-app-shell" @keydown.escape="closeAll">
    <!-- App nav — identical chrome to Home.vue -->
    <nav class="tc-app-nav">
      <span class="tc-app-nav-brand">TimeCapture · Draft-first preview</span>
      <div class="tc-app-nav-spacer"></div>
    </nav>

    <!-- Scrollable page body — slices stacked -->
    <div style="flex:1;overflow-y:auto;background:var(--bg-canvas)">
      <div style="max-width:1240px;margin:0 auto;padding:var(--s-40) var(--s-24)">

        <!-- ──────────────────────────────────────────────────────────── -->
        <!-- Slice 1: Triage default (draft-first) -->
        <!-- ──────────────────────────────────────────────────────────── -->
        <section style="margin-bottom:var(--s-40)">
          <h2 style="font-size:var(--t-18);font-weight:var(--w-semibold);color:var(--fg-primary);margin-bottom:var(--s-4)">1. Triage default state</h2>
          <p style="font-size:var(--t-13);color:var(--fg-tertiary);margin-bottom:var(--s-16);line-height:var(--lh-13)">
            Grid is the main canvas. Click <strong>Events</strong> in the period header to toggle the inspector drawer.
            Click a draft row WBS to open the row-detail modal. Click the <strong>?</strong> on the 1.5h cell to see the agent's reasoning.
          </p>

          <!-- Period shell — subheader + controls + panels -->
          <div
            style="
              background:var(--bg-surface);
              border:1px solid var(--stroke-1);
              border-radius:var(--r-lg);
              box-shadow:var(--shadow-4);
              overflow:hidden;
              display:grid;
            "
            :style="slice1DrawerOpen ? 'grid-template-columns:1fr 380px' : 'grid-template-columns:1fr'"
          >
            <!-- Left column -->
            <div style="min-width:0">
              <!-- Subheader -->
              <div class="tc-triage-subheader">
                <div class="d-flex align-items-baseline gap-2 flex-wrap">
                  <span style="font-size:var(--t-18);font-weight:var(--w-semibold);color:var(--fg-primary)">Apr 16–30</span>
                  <span style="color:var(--fg-disabled)">·</span>
                  <span style="font-size:var(--t-13);color:var(--fg-tertiary)">12.5h drafted</span>
                  <span style="color:var(--fg-disabled)">·</span>
                  <span style="font-size:var(--t-13);color:var(--fg-tertiary)">2 to confirm</span>
                </div>
                <div
                  class="tc-triage-progress-bar"
                  style="--tc-triage-progress-pct:31.25%"
                >
                  <div class="tc-triage-progress-fill" role="progressbar" aria-valuenow="5" aria-valuemax="16"></div>
                </div>
                <div class="tc-triage-spacer"></div>
                <div class="dropdown">
                  <button
                    class="btn btn-outline-secondary btn-sm dropdown-toggle"
                    type="button"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                  >Regenerate</button>
                  <ul class="dropdown-menu dropdown-menu-end">
                    <li><button class="dropdown-item" type="button">Update draft</button></li>
                    <li><button class="dropdown-item" type="button">Re-draft everything</button></li>
                  </ul>
                </div>
                <button type="button" class="btn btn-primary btn-sm">Verify &amp; Save</button>
              </div>

              <!-- Grid -->
              <DfGrid
                :show-events-toggle="true"
                :events-open="slice1DrawerOpen"
                @row-click="openRowDetail"
                @toggle-events="slice1DrawerOpen = !slice1DrawerOpen"
              />

              <!-- Needs input -->
              <DfNeedsInput />
            </div>

            <!-- Right column: events inspector drawer -->
            <DfEventsInspector
              v-if="slice1DrawerOpen"
              @close="slice1DrawerOpen = false"
            />
          </div>
        </section>

        <hr style="border:none;border-top:1px solid var(--stroke-1);margin:var(--s-40) 0" />

        <!-- ──────────────────────────────────────────────────────────── -->
        <!-- Slice 2: Events inspector open (static demo) -->
        <!-- ──────────────────────────────────────────────────────────── -->
        <section style="margin-bottom:var(--s-40)">
          <h2 style="font-size:var(--t-18);font-weight:var(--w-semibold);color:var(--fg-primary);margin-bottom:var(--s-4)">2. Events inspector open</h2>
          <p style="font-size:var(--t-13);color:var(--fg-tertiary);margin-bottom:var(--s-16);line-height:var(--lh-13)">
            Inspector drawer side-by-side with the grid. Filter pills scope the list to Attributed / Needs input / Filtered.
            The Acme row is highlighted because "Acme weekly sync" is selected in the drawer.
          </p>

          <div
            style="
              background:var(--bg-surface);
              border:1px solid var(--stroke-1);
              border-radius:var(--r-lg);
              box-shadow:var(--shadow-4);
              overflow:hidden;
              display:grid;
              grid-template-columns:1fr 380px;
            "
          >
            <div style="min-width:0">
              <div class="tc-triage-subheader">
                <div class="d-flex align-items-baseline gap-2 flex-wrap">
                  <span style="font-size:var(--t-18);font-weight:var(--w-semibold);color:var(--fg-primary)">Apr 16–30</span>
                  <span style="color:var(--fg-disabled)">·</span>
                  <span style="font-size:var(--t-13);color:var(--fg-tertiary)">12.5h drafted</span>
                </div>
                <div class="tc-triage-spacer"></div>
                <button type="button" class="btn btn-primary btn-sm">Verify &amp; Save</button>
              </div>
              <DfGrid
                highlight-wbs="2024-ACME-001"
                :show-events-toggle="true"
                :events-open="true"
                @row-click="openRowDetail"
              />
              <DfNeedsInput />
            </div>
            <DfEventsInspector selected-event-id="ev-4" />
          </div>
        </section>

        <hr style="border:none;border-top:1px solid var(--stroke-1);margin:var(--s-40) 0" />

        <!-- ──────────────────────────────────────────────────────────── -->
        <!-- Slice 3: Row detail modal (static demo) -->
        <!-- ──────────────────────────────────────────────────────────── -->
        <section style="margin-bottom:var(--s-40)">
          <h2 style="font-size:var(--t-18);font-weight:var(--w-semibold);color:var(--fg-primary);margin-bottom:var(--s-4)">3. Row detail</h2>
          <p style="font-size:var(--t-13);color:var(--fg-tertiary);margin-bottom:var(--s-16);line-height:var(--lh-13)">
            Note is the hero. Click <strong>Summarised from N events</strong> to expand the provenance list.
            Also opens as a modal when clicking a draft row in slices 1 or 2.
          </p>
          <DfRowDetail />
        </section>

        <hr style="border:none;border-top:1px solid var(--stroke-1);margin:var(--s-40) 0" />

        <!-- ──────────────────────────────────────────────────────────── -->
        <!-- Slice 4: Cell-badge popover -->
        <!-- ──────────────────────────────────────────────────────────── -->
        <section style="margin-bottom:var(--s-40)">
          <h2 style="font-size:var(--t-18);font-weight:var(--w-semibold);color:var(--fg-primary);margin-bottom:var(--s-4)">4. Cell-badge popover</h2>
          <p style="font-size:var(--t-13);color:var(--fg-tertiary);margin-bottom:var(--s-16);line-height:var(--lh-13)">
            Click <strong>?</strong> on a cell → reasoning popover (left). Click <strong>Change project</strong> from reasoning → picker (right).
          </p>
          <DfCellBadgePopover />
        </section>

        <hr style="border:none;border-top:1px solid var(--stroke-1);margin:var(--s-40) 0" />

        <!-- ──────────────────────────────────────────────────────────── -->
        <!-- Slice 5: Mobile scoped queue -->
        <!-- ──────────────────────────────────────────────────────────── -->
        <section>
          <h2 style="font-size:var(--t-18);font-weight:var(--w-semibold);color:var(--fg-primary);margin-bottom:var(--s-4)">5. Mobile scoped queue</h2>
          <p style="font-size:var(--t-13);color:var(--fg-tertiary);margin-bottom:var(--s-16);line-height:var(--lh-13)">
            Phone-frame (~390 × 780px). Next card peeks behind the hero. Two touch-target action buttons.
          </p>
          <DfMobileQueue />
        </section>

      </div>
    </div>

    <!-- Row-detail modal (from grid row click in slice 1 / 2) -->
    <Teleport to="body">
      <div
        v-if="rowDetailWbs"
        style="position:fixed;inset:0;background:rgba(0,0,0,.35);z-index:1050"
        @click="rowDetailWbs = null"
      />
      <div
        v-if="rowDetailWbs"
        style="
          position:fixed;
          top:50%;
          left:50%;
          transform:translate(-50%,-50%);
          z-index:1060;
          width:min(600px, calc(100vw - var(--s-32)));
          max-height:calc(100vh - var(--s-48));
          background:var(--bg-surface);
          border:1px solid var(--stroke-1);
          border-radius:var(--r-lg);
          box-shadow:var(--shadow-28);
          overflow:hidden;
          display:flex;
          flex-direction:column;
        "
        role="dialog"
        aria-modal="true"
      >
        <DfRowDetail
          :closeable="true"
          :embedded="true"
          @close="rowDetailWbs = null"
        />
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import DfGrid from '../components/draft-first/DfGrid.vue';
import DfNeedsInput from '../components/draft-first/DfNeedsInput.vue';
import DfEventsInspector from '../components/draft-first/DfEventsInspector.vue';
import DfRowDetail from '../components/draft-first/DfRowDetail.vue';
import DfCellBadgePopover from '../components/draft-first/DfCellBadgePopover.vue';
import DfMobileQueue from '../components/draft-first/DfMobileQueue.vue';

const slice1DrawerOpen = ref(false);
const rowDetailWbs = ref<string | null>(null);

function openRowDetail(wbs: string) {
  rowDetailWbs.value = wbs;
}

function closeAll() {
  slice1DrawerOpen.value = false;
  rowDetailWbs.value = null;
}
</script>
